import React from 'react';

import { Icon } from '../components/base/Icon';
import { cn } from '../components/cn';

import { CRM_NAV } from './CrmNav';
import type { CrmScreenKey } from './CrmApiPort';

/**
 * เมนูย่อยของโมดูล — ใช้เฉพาะตอน host ให้มาแค่ "เมนูเดียว"
 *
 * host ฝั่ง HIS ให้พื้นที่มาเมนูเดียวชื่อ MeCRM แต่โมดูลมี 14 หน้าจอ
 * จึงต้องมีเมนูของตัวเองข้างใน ไม่ใช่ผูกกับ URL เพราะ host ไม่มี URL ให้เรา
 *
 * ⚠️ กว้าง 200px — บวกเมนูของ host อีก 150–250px แล้วยังเหลือให้ตารางพอ
 *    ที่ 1440px: 1440 − 250 − 200 = 990px ซึ่งต่ำกว่า --bo-min-width (1120px)
 *    → ยุบได้ และยุบเป็นค่าเริ่มต้นเมื่อหน้าต่างแคบ
 */
const WIDTH = 200;
const COLLAPSED = 56;

export interface CrmSideNavProps {
  current: CrmScreenKey;
  onSelect: (screen: CrmScreenKey) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export const CrmSideNav: React.FC<CrmSideNavProps> = ({
  current,
  onSelect,
  collapsed,
  onToggleCollapsed,
}) => (
  <nav
    aria-label="เมนูระบบหลังบ้าน CRM"
    style={{ width: collapsed ? COLLAPSED : WIDTH, minWidth: collapsed ? COLLAPSED : WIDTH }}
    className="flex shrink-0 flex-col gap-[var(--space-2)] border-r border-[var(--line-200)] bg-[var(--surface-0)] py-[var(--space-2)]"
  >
    <button
      type="button"
      onClick={onToggleCollapsed}
      aria-expanded={!collapsed}
      className="mx-[var(--space-2)] inline-flex min-h-[var(--hit-control)] items-center justify-center gap-[var(--space-1)] rounded-[var(--radius-sm)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
    >
      <Icon name={collapsed ? 'chevron_right' : 'chevron_left'} size={20} />
      {!collapsed && <span className="font-thai text-table-sm">ย่อเมนู</span>}
    </button>

    {CRM_NAV.map((group) => (
      <div key={group.title} className="flex flex-col gap-[2px]">
        {!collapsed && (
          <div className="px-[var(--space-3)] pt-[var(--space-2)] font-thai text-micro font-[var(--weight-semibold)] uppercase text-[var(--ink-600)]">
            {group.title}
          </div>
        )}
        <ul className="flex flex-col gap-[2px] px-[var(--space-2)]">
          {group.items.map((item) => {
            const active = item.screen === current;
            return (
              <li key={item.screen}>
                <button
                  type="button"
                  onClick={() => onSelect(item.screen)}
                  aria-current={active ? 'page' : undefined}
                  title={collapsed ? item.label : undefined}
                  className={cn(
                    'font-thai text-form leading-form flex w-full min-h-[var(--hit-primary)] items-center gap-[var(--space-2)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-left',
                    'outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]',
                    collapsed && 'justify-center',
                    active
                      ? 'bg-[var(--crm-tint-strong)] font-[var(--weight-semibold)] text-[var(--crm-chrome)]'
                      : 'text-[var(--ink-700)] hover:bg-[var(--surface-100)]',
                  )}
                >
                  <Icon name={item.icon} size={20} filled={active} />
                  {!collapsed && <span className="bo-truncate">{item.label}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    ))}
  </nav>
);
